/**
 * Every "how fresh is this" sentence this module renders, and the ONE
 * `Intl.DateTimeFormat('en-GB', …)` behind them all.
 *
 * Extracted here at Ticket 2 (#425) once a FOURTH call site needed the
 * identical formatter — `Async.tsx`'s own platform-level default, `Accounts
 * .tsx`'s `RowHealthNote`, `AccountsSummary.tsx`'s Connections tile, and now
 * `InstitutionsPanel.tsx`. Architecture review: four copies serving ONE
 * feature (this module's accounts) is the point a small shared file stops
 * being premature. `Async.tsx`'s own default stays where it is — it is
 * platform-wide, not accounts-specific — this file is for sentences that
 * name a subject particular to `src/routes/accounts/`.
 *
 * **Every sentence a nested `<Async>` on this module's pages supplies must
 * name its OWN subject, never repeat another surface's wording.** Two
 * `<Async>`s can sit on one page (`AccountsSummary.tsx`'s Connections tile
 * alongside the page's own `surface="accounts"`, and now `InstitutionsPanel
 * .tsx`'s third), and for a single-institution household every surface's
 * `asOf` traces to the same `last_synced_at` — so two surfaces with the
 * SAME sentence render byte-identical text twice on one page, which is
 * exactly what broke `account-detail.spec.ts:377` before the Connections
 * tile got its own (architect review, #425, blocking).
 */
const DATE = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

export function formatStaleDate(date: Date): string {
  return DATE.format(date)
}

/**
 * Coarse relative time for a "last sync" clause — minutes for the first
 * hour, then hours, then days. Moved from `Accounts.tsx` (the header
 * subtitle's own copy) once `InstitutionsPanel.tsx` needed the identical
 * bucketing for a row's own "synced … ago" clause — no design source pins an
 * exact format for either caller (unlike the detail hero's "Synced 4 min
 * ago" pill, Ticket 4's own job), so both stay a plain, honest bucketing
 * rather than invented precision.
 */
export function formatRelative(date: Date): string {
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60_000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/**
 * `AccountsSummary.tsx`'s Connections tile — names its own subject rather
 * than `<Async>`'s default "Showing data from …", which the page's own
 * `surface="accounts"` banner already renders (see this file's own
 * docblock).
 */
export function connectionsStaleLabel(asOf: Date): string {
  return `Connection status from ${formatStaleDate(asOf)}`
}

/**
 * `InstitutionsPanel.tsx`'s own nested surface — a THIRD sentence, distinct
 * from both the page's "Showing data from …" and the Connections tile's
 * "Connection status from …" (#425 Ticket 2).
 */
export function institutionsStaleLabel(asOf: Date): string {
  return `Institution data from ${formatStaleDate(asOf)}`
}

/**
 * `Connections.tsx`'s own NESTED `surface="accounts"` (Task 5, #442) — the
 * page-level `surface="aggregator-items"` already reuses `connectionsStaleLabel`
 * above ("Connection status from …"), so the account-balances half needs its
 * own sentence rather than repeating it. Same trap `account-detail.spec.ts`
 * caught before `connectionsStaleLabel` existed: a single-institution
 * household has both surfaces' `asOf` trace to the same `last_synced_at`, so
 * two identical sentences would render byte-identically twice and a
 * `getByText` would resolve to two elements.
 */
export function accountBalancesStaleLabel(asOf: Date): string {
  return `Account balances from ${formatStaleDate(asOf)}`
}
